/**
 * FlyCastOverlay
 *
 * Full-screen overlay for the fly cast tracing minigame.
 * Appears when the player clicks Fish while a fly rod is equipped.
 * Contains the bezier curve circle, trace input handling, and result display.
 * Fish is caught (or not) immediately when the trace completes — no fishing timer.
 */

import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import type { FlyCastResult, FlyCastTier, FishInstance, FishDefinition } from '@/types';
import { QualityColors, QualityNames, QualityMultipliers, GameEvent, EquipmentSlot } from '@/types';
import { generateFlyCurve } from '@/utils/flyCurve';
import { getFlyCastBonusForTier } from '@/utils/flyFishing';
import { formatWeight, formatLength } from '@/utils';
import { useFlyCastTrace } from '@/hooks/useFlyCastTrace';
import { FlyCastCurveRenderer } from './FlyCastCurveRenderer';
import { fishingEngine } from '@/systems';
import { eventBus } from '@/services/EventBus';
import { useEquippedGear } from '@/state';
import { EQUIPMENT_DATA } from '@/data';

interface FlyCastOverlayProps {
  visible: boolean;
  onDismiss: () => void;
}

/** Tier display configuration */
const TIER_CONFIG: Record<FlyCastTier, { label: string; color: string; glow: string }> = {
  perfect: {
    label: 'Perfect Cast!',
    color: 'text-yellow-300',
    glow: '0 0 20px rgba(250, 204, 21, 0.6), 0 0 40px rgba(250, 204, 21, 0.3)',
  },
  good: {
    label: 'Good Cast!',
    color: 'text-blue-300',
    glow: '0 0 16px rgba(147, 197, 253, 0.5), 0 0 32px rgba(96, 165, 250, 0.2)',
  },
  miss: {
    label: 'Miss',
    color: 'text-red-400/80',
    glow: 'none',
  },
};

/** Auto-dismiss delay after result shown (ms) */
const RESULT_DISMISS_DELAY = 2000;

/** Caught fish data captured from the FISH_CAUGHT event */
interface CaughtFishInfo {
  fish: FishInstance;
  definition: FishDefinition;
}

export function FlyCastOverlay({ visible, onDismiss }: FlyCastOverlayProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<FlyCastResult | null>(null);
  const [caughtFish, setCaughtFish] = useState<CaughtFishInfo | null>(null);

  const equippedGear = useEquippedGear();

  // Generate a new curve each time the overlay becomes visible
  const curve = useMemo(() => {
    if (!visible) return null;
    return generateFlyCurve();
  }, [visible]);

  // Determine current fly pattern from equipped bait
  const flyPatternId = useMemo(() => {
    if (!visible) return undefined;
    const baitId = equippedGear[EquipmentSlot.BAIT];
    if (!baitId) return undefined;
    const baitDef = EQUIPMENT_DATA[baitId];
    return baitDef?.flyPatternId;
  }, [visible, equippedGear]);

  const handleComplete = useCallback(
    (castResult: FlyCastResult) => {
      setResult(castResult);
      setShowResult(true);

      // Resolve the fly cast immediately — fish is caught (or not) right now.
      // No fishing timer — the fly cast minigame IS the entire fishing action.
      const bonus = getFlyCastBonusForTier(castResult.tier, castResult.accuracy, flyPatternId);

      // Capture FISH_CAUGHT event synchronously (fires during resolveFlyCast)
      let caught: CaughtFishInfo | null = null;
      const unsub = eventBus.on(GameEvent.FISH_CAUGHT, (payload: { fish: FishInstance; definition: FishDefinition }) => {
        caught = { fish: payload.fish, definition: payload.definition };
      });

      fishingEngine.resolveFlyCast(bonus);
      unsub(); // Remove one-shot listener

      setCaughtFish(caught);

      // Emit event for quest tracking / analytics
      eventBus.emit(GameEvent.FLY_CAST_COMPLETED, {
        tier: castResult.tier,
        accuracy: castResult.accuracy,
        flyPatternId,
      });

      // Auto-dismiss after showing result
      setTimeout(() => {
        setShowResult(false);
        setResult(null);
        setCaughtFish(null);
        onDismiss();
      }, RESULT_DISMISS_DELAY);
    },
    [flyPatternId, onDismiss],
  );

  const {
    traceState,
    currentAccuracy,
    traceProgress,
    tracePointsRef,
    lastSampleScore,
  } = useFlyCastTrace({
    curve: curve!,
    containerRef,
    onComplete: handleComplete,
    enabled: visible && curve !== null,
  });

  // Reset state when overlay closes
  useEffect(() => {
    if (!visible) {
      setShowResult(false);
      setResult(null);
      setCaughtFish(null);
    }
  }, [visible]);

  if (!visible || !curve) return null;

  const tierConfig = result ? TIER_CONFIG[result.tier] : null;

  // Compute gold value for caught fish
  const goldValue = caughtFish
    ? Math.round(
        caughtFish.definition.baseValue *
        (QualityMultipliers[caughtFish.fish.quality] ?? 1)
      )
    : 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex flex-col items-center justify-center px-4">
      {/* Trace area */}
      <div
        ref={containerRef}
        className="relative w-[320px] h-[320px] max-w-[90vw] max-h-[90vw] flex-shrink-0"
        style={{ touchAction: 'none' }}
      >
        <FlyCastCurveRenderer
          curve={curve}
          tracePointsRef={tracePointsRef}
          traceState={traceState}
          lastSampleScore={lastSampleScore}
          currentAccuracy={currentAccuracy}
          traceProgress={traceProgress}
        />

        {/* Instruction text when idle */}
        {traceState === 'idle' && !showResult && (
          <div className="absolute bottom-[-48px] left-0 right-0 text-center">
            <p className="text-green-300 font-bold text-sm animate-pulse">
              Touch the green dot and trace the line!
            </p>
          </div>
        )}

        {/* Progress bar during tracing */}
        {traceState === 'tracing' && (
          <div className="absolute bottom-[-40px] left-0 right-0 px-4">
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-100"
                style={{
                  width: `${traceProgress * 100}%`,
                  backgroundColor:
                    currentAccuracy >= 0.85
                      ? '#4ade80'
                      : currentAccuracy >= 0.6
                        ? '#fbbf24'
                        : '#f87171',
                }}
              />
            </div>
          </div>
        )}

        {/* Result banner */}
        {showResult && tierConfig && result && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <p
                className={`${tierConfig.color} font-black text-3xl`}
                style={{
                  textShadow: tierConfig.glow,
                  animation: 'flyCastResult 0.5s ease-out forwards',
                }}
              >
                {tierConfig.label}
              </p>
              <p className="text-gray-300 text-sm mt-1">
                {Math.round(result.accuracy * 100)}% accuracy
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Caught fish card — below the trace area, centered by flexbox */}
      {showResult && caughtFish && (
        <div
          className="mt-16 w-[320px] max-w-[90vw] flex-shrink-0"
          style={{ animation: 'flyCatchSlideUp 0.4s ease-out forwards' }}
        >
          <div
            className="rounded-xl border-2 bg-gray-900/95 backdrop-blur-sm px-4 py-3"
            style={{ borderColor: QualityColors[caughtFish.fish.quality] }}
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl">{caughtFish.definition.emoji || '🐟'}</span>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-bold text-white truncate">
                  {caughtFish.definition.name}
                </h3>
                <span
                  className="text-sm font-semibold"
                  style={{ color: QualityColors[caughtFish.fish.quality] }}
                >
                  {QualityNames[caughtFish.fish.quality]}
                </span>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-yellow-400 font-bold text-lg">
                  {goldValue}g
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
              <span>{formatLength(caughtFish.fish.length)}</span>
              <span className="text-gray-600">|</span>
              <span>{formatWeight(caughtFish.fish.weight)}</span>
              {caughtFish.fish.isWild && (
                <>
                  <span className="text-gray-600">|</span>
                  <span className="text-emerald-400 font-medium">Wild</span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* "Fish got away" message when cast resolves but no catch */}
      {showResult && !caughtFish && result && (
        <div
          className="mt-16 flex-shrink-0"
          style={{ animation: 'flyCatchSlideUp 0.4s ease-out forwards' }}
        >
          <p className="text-gray-400 text-sm font-medium">
            Fish got away...
          </p>
        </div>
      )}

      {/* Keyframe animations */}
      <style>{`
        @keyframes flyCastResult {
          0%   { transform: scale(0.3); opacity: 0; }
          50%  { transform: scale(1.15); opacity: 1; }
          100% { transform: scale(1.0); opacity: 1; }
        }
        @keyframes flyCatchSlideUp {
          0%   { transform: translateY(30px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
